import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { subDays, format } from 'date-fns';
import { useJobs } from '@/hooks/useJobs';
import { useClients } from '@/hooks/useClients';
import { PHASE_TO_STATUSES } from '../components/JobPhaseSelector';
import type { JobPhase } from '../components/JobPhaseSelector';
import type { JobStatus, JobCardStatus, JobInvoiceStatus } from '@jobos/shared/types/job';
import { formatClientName } from '@jobos/shared';
import {
  buildQueryFromState,
  normalizeQuery,
  parseJobListQuery,
  type JobFilters,
} from '../utils/jobListFilters';
import {
  useJobFilterProfiles,
  useSaveJobFilterProfile,
  useSetDefaultJobFilterProfile,
  useDeleteJobFilterProfile,
} from '@/hooks/useJobFilterProfiles';

export type DatePreset = 'last7' | 'last30' | 'last90' | '';

/** Drawer-only controls that stay pending until "Apply Filters" is pressed. */
export interface DrawerDraft {
  search: string;
  showArchived: boolean | 'only';
  showDeleted: boolean;
}

export interface PresetChip {
  id: string;
  label: string;
  apply: (args: {
    setSelectedJobCardStatuses: (next: JobCardStatus[]) => void;
    setSelectedInvoiceStatuses: (next: JobInvoiceStatus[]) => void;
  }) => void;
  isActive: (state: {
    selectedJobCardStatuses: string[];
    selectedInvoiceStatuses: string[];
  }) => boolean;
}

const arraysEqual = <T,>(a: T[], b: T[]) =>
  a.length === b.length && a.every((v, i) => v === b[i]);

export const PRESET_CHIPS: PresetChip[] = [
  {
    id: 'ready_to_invoice',
    label: 'Ready to invoice',
    apply: ({ setSelectedJobCardStatuses, setSelectedInvoiceStatuses }) => {
      setSelectedJobCardStatuses(['ready']);
      setSelectedInvoiceStatuses(['not_invoiced']);
    },
    isActive: ({ selectedJobCardStatuses, selectedInvoiceStatuses }) =>
      arraysEqual(selectedJobCardStatuses, ['ready']) &&
      arraysEqual(selectedInvoiceStatuses, ['not_invoiced']),
  },
  {
    id: 'awaiting_payment',
    label: 'Awaiting payment',
    // invoice_sent ALONE is the awaiting-payment state. partially_paid means
    // the opposite under the action-prompt ladder: nothing is outstanding and
    // the job is waiting on someone to issue the next invoice. Including it
    // would fill a chase-the-money worklist with jobs that owe nothing.
    apply: ({ setSelectedInvoiceStatuses }) => setSelectedInvoiceStatuses(['invoice_sent']),
    isActive: ({ selectedInvoiceStatuses }) =>
      arraysEqual(selectedInvoiceStatuses, ['invoice_sent']),
  },
  {
    id: 'paid',
    label: 'Paid',
    apply: ({ setSelectedInvoiceStatuses }) => setSelectedInvoiceStatuses(['paid']),
    isActive: ({ selectedInvoiceStatuses }) => arraysEqual(selectedInvoiceStatuses, ['paid']),
  },
];

const buildFiltersFromState = ({
  phase,
  statuses,
  jobCardStatuses,
  invoiceStatuses,
  projectTypes,
  clientIds,
  archived,
  sortField,
  sortDirection,
  showDeleted,
  dateField,
  dateFrom,
  dateTo,
  search,
}: {
  phase: JobPhase;
  statuses: string[];
  jobCardStatuses: string[];
  invoiceStatuses: string[];
  projectTypes: string[];
  clientIds: string[];
  archived: boolean | 'only';
  sortField?: string;
  sortDirection?: 'asc' | 'desc';
  showDeleted?: boolean;
  dateField?: string;
  dateFrom?: string;
  dateTo?: string;
  search?: string;
}): JobFilters => {
  const newFilters: JobFilters = {};
  const phaseStatuses = PHASE_TO_STATUSES[phase];
  if (phaseStatuses !== null) {
    newFilters.status = phaseStatuses;
  }

  if (statuses.length > 0) {
    if (newFilters.status) {
      const combinedStatuses = (statuses as JobStatus[]).filter(
        status => newFilters.status?.includes(status)
      );
      newFilters.status = combinedStatuses.length > 0 ? combinedStatuses : newFilters.status;
    } else {
      newFilters.status = statuses as JobStatus[];
    }
  }

  if (projectTypes.length > 0) {
    newFilters.projectType = projectTypes as string[];
  }

  if (jobCardStatuses.length > 0) {
    newFilters.jobCardStatus = jobCardStatuses as JobCardStatus[];
  }

  if (invoiceStatuses.length > 0) {
    newFilters.invoiceStatus = invoiceStatuses as JobInvoiceStatus[];
  }

  if (clientIds.length > 0) {
    newFilters.clientId = clientIds;
  }

  if (archived !== false) {
    newFilters.isArchived = archived;
  }

  if (showDeleted) {
    newFilters.showDeleted = 'only';
  }

  if (sortField) {
    newFilters.sortField = sortField;
  }

  if (sortDirection) {
    newFilters.sortDirection = sortDirection;
  }

  if (dateField) {
    newFilters.dateField = dateField;
  }
  if (dateFrom) {
    newFilters.dateFrom = dateFrom;
  }
  if (dateTo) {
    newFilters.dateTo = dateTo;
  }
  if (search && search.trim()) {
    newFilters.search = search.trim();
  }

  return newFilters;
};

const isSameArray = (left: string[], right: string[]) =>
  left.length === right.length && left.every((value, index) => value === right[index]);

const areFiltersEqual = (left: JobFilters, right: JobFilters) => {
  const leftKeys = Object.keys(left);
  const rightKeys = Object.keys(right);
  if (leftKeys.length !== rightKeys.length) return false;
  return leftKeys.every(key => {
    const leftValue = left[key as keyof JobFilters];
    const rightValue = right[key as keyof JobFilters];
    if (Array.isArray(leftValue) && Array.isArray(rightValue)) {
      return isSameArray(leftValue, rightValue);
    }
    return leftValue === rightValue;
  });
};

export function useJobListFilters() {
  const location = useLocation();
  const navigate = useNavigate();
  const hasAppliedDefaultProfile = useRef(false);
  const [filters, setFilters] = useState<JobFilters>({});
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [selectedPhase, setSelectedPhase] = useState<JobPhase>('all');
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);
  const [selectedJobCardStatuses, setSelectedJobCardStatuses] = useState<string[]>([]);
  const [selectedInvoiceStatuses, setSelectedInvoiceStatuses] = useState<string[]>([]);
  const [selectedClients, setSelectedClients] = useState<string[]>([]);
  const [selectedProjectTypes, setSelectedProjectTypes] = useState<string[]>([]);
  const [searchText, setSearchText] = useState('');
  const [dateField, setDateField] = useState('statusDate');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [datePreset, setDatePreset] = useState<DatePreset>('');
  const [showArchived, setShowArchived] = useState<boolean | 'only'>(false);
  const [showDeleted, setShowDeleted] = useState(false);
  const [activeProfileName, setActiveProfileName] = useState('');
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  const locationSearchRef = useRef(location.search);
  useEffect(() => {
    locationSearchRef.current = location.search;
  }, [location.search]);

  useEffect(() => {
    const trimmed = searchText.trim();
    const handle = setTimeout(() => {
      setFilters(prev => {
        if ((prev.search ?? '') === trimmed) return prev;
        const next = { ...prev };
        if (trimmed) {
          next.search = trimmed;
        } else {
          delete next.search;
        }
        return next;
      });

      const params = new URLSearchParams(locationSearchRef.current);
      const currentSearch = params.get('search') ?? '';
      if (currentSearch === trimmed) return;
      if (trimmed) {
        params.set('search', trimmed);
      } else {
        params.delete('search');
      }
      const nextQuery = params.toString();
      const nextSearch = nextQuery ? `?${nextQuery}` : '';
      if (normalizeQuery(locationSearchRef.current) === normalizeQuery(nextSearch)) return;
      navigate({ pathname: location.pathname, search: nextSearch }, { replace: true });
    }, 500);
    return () => clearTimeout(handle);
  }, [searchText, location.pathname, navigate]);

  const { data: jobs, isLoading, isFetching, error } = useJobs(filters);
  const { data: clients } = useClients();
  const { data: filterProfiles = [], isLoading: profilesLoading } = useJobFilterProfiles();
  const saveProfileMutation = useSaveJobFilterProfile();
  const setDefaultMutation = useSetDefaultJobFilterProfile();
  const deleteProfileMutation = useDeleteJobFilterProfile();

  const availableClients = useMemo(() => {
    if (!clients) return [];
    const clientNames = clients.map(client => formatClientName(client.name, client.firstName, client.lastName));
    return Array.from(new Set(clientNames)).filter(Boolean).sort();
  }, [clients]);

  const clientNameToId = useMemo(() => {
    if (!clients) return new Map<string, string>();
    const map = new Map<string, string>();
    clients.forEach(client => {
      const displayName = formatClientName(client.name, client.firstName, client.lastName);
      if (displayName) {
        map.set(displayName, client.id);
      }
    });
    return map;
  }, [clients]);

  const updateQueryString = (nextQuery: string, replace = true) => {
    if (normalizeQuery(location.search) === normalizeQuery(nextQuery)) {
      return;
    }
    navigate({ pathname: location.pathname, search: nextQuery }, { replace });
  };

  const openDrawer = () => {
    setIsDrawerOpen(true);
    setTimeout(() => setIsAnimating(true), 10);
  };

  const closeDrawer = () => {
    setIsAnimating(false);
    setTimeout(() => setIsDrawerOpen(false), 300);
  };


  const handleSort = (field: string) => {
    setFilters(prev => {
      let nextSortField = field;
      let nextSortDirection: 'asc' | 'desc' = 'asc';
      if (prev.sortField === field) {
        nextSortDirection = prev.sortDirection === 'asc' ? 'desc' : 'asc';
      }

      const nextFilters = {
        ...prev,
        sortField: nextSortField,
        sortDirection: nextSortDirection,
      };

      updateQueryString(
        buildQueryFromState({
          phase: selectedPhase,
          statuses: selectedStatuses,
          jobCardStatuses: selectedJobCardStatuses,
          invoiceStatuses: selectedInvoiceStatuses,
          projectTypes: selectedProjectTypes,
          clientIds: nextFilters.clientId || [],
          archived: showArchived,
          sortField: nextSortField,
          sortDirection: nextSortDirection,
          search: searchText,
          showDeleted,
          dateField: dateField !== 'statusDate' ? dateField : undefined,
          dateFrom: dateFrom || undefined,
          dateTo: dateTo || undefined,
        })
      );

      return nextFilters;
    });
  };

  const applyFilters = (draft?: DrawerDraft) => {
    const clientIds = selectedClients
      .map(name => clientNameToId.get(name))
      .filter((id): id is string => id !== undefined);

    const nextSearch = draft?.search ?? searchText;
    const nextArchived = draft?.showArchived ?? showArchived;
    const nextDeleted = draft?.showDeleted ?? showDeleted;

    setSearchText(nextSearch);
    setShowArchived(nextArchived);
    setShowDeleted(nextDeleted);

    const newFilters = buildFiltersFromState({
      phase: selectedPhase,
      statuses: selectedStatuses,
      jobCardStatuses: selectedJobCardStatuses,
      invoiceStatuses: selectedInvoiceStatuses,
      projectTypes: selectedProjectTypes,
      clientIds,
      archived: nextArchived,
      sortField: filters.sortField,
      sortDirection: filters.sortDirection,
      search: nextSearch,
      showDeleted: nextDeleted,
      dateField: dateField !== 'statusDate' ? dateField : undefined,
      dateFrom: dateFrom || undefined,
      dateTo: dateTo || undefined,
    });

    setFilters(newFilters);
    updateQueryString(
      buildQueryFromState({
        phase: selectedPhase,
        statuses: selectedStatuses,
        jobCardStatuses: selectedJobCardStatuses,
        invoiceStatuses: selectedInvoiceStatuses,
        projectTypes: selectedProjectTypes,
        clientIds,
        archived: nextArchived,
        sortField: newFilters.sortField,
        sortDirection: newFilters.sortDirection,
        search: nextSearch,
        showDeleted: nextDeleted,
        dateField: dateField !== 'statusDate' ? dateField : undefined,
        dateFrom: dateFrom || undefined,
        dateTo: dateTo || undefined,
      })
    );
    closeDrawer();
  };

  const clearFilters = () => {
    setSelectedPhase('all');
    setSelectedStatuses([]);
    setSelectedJobCardStatuses([]);
    setSelectedInvoiceStatuses([]);
    setSelectedClients([]);
    setSelectedProjectTypes([]);
    setSearchText('');
    setShowArchived(false);
    setShowDeleted(false);
    setDateField('statusDate');
    setDateFrom('');
    setDateTo('');
    setDatePreset('');
    setFilters({});
    setActiveProfileName('');
    updateQueryString(
      buildQueryFromState({
        phase: 'all',
        statuses: [],
        jobCardStatuses: [],
        invoiceStatuses: [],
        projectTypes: [],
        clientIds: [],
        archived: false,
        sortField: undefined,
        sortDirection: undefined,
        search: '',
        showDeleted: false,
      })
    );
    closeDrawer();
  };

  const handlePhaseChange = (phase: JobPhase) => {
    setSelectedPhase(phase);

    const clientIds = selectedClients
      .map(name => clientNameToId.get(name))
      .filter((id): id is string => id !== undefined);

    const newFilters = buildFiltersFromState({
      phase,
      statuses: [],
      jobCardStatuses: [],
      invoiceStatuses: [],
      projectTypes: selectedProjectTypes,
      clientIds,
      archived: showArchived,
      sortField: filters.sortField,
      sortDirection: filters.sortDirection,
      showDeleted,
      dateField: dateField !== 'statusDate' ? dateField : undefined,
      dateFrom: dateFrom || undefined,
      dateTo: dateTo || undefined,
    });

    setFilters(newFilters);
    updateQueryString(
      buildQueryFromState({
        phase,
        statuses: [],
        jobCardStatuses: [],
        invoiceStatuses: [],
        projectTypes: selectedProjectTypes,
        clientIds: newFilters.clientId || [],
        archived: showArchived,
        sortField: newFilters.sortField,
        sortDirection: newFilters.sortDirection,
        search: searchText,
        showDeleted,
        dateField: dateField !== 'statusDate' ? dateField : undefined,
        dateFrom: dateFrom || undefined,
        dateTo: dateTo || undefined,
      })
    );
  };

  const handleSearchChange = (value: string) => {
    setSearchText(value);
  };

  const handleArchivedChange = (value: boolean | 'only') => {
    setShowArchived(value);
    setFilters(prev => {
      const nextFilters = {
        ...prev,
        isArchived: value === false ? undefined : value,
      };
      updateQueryString(
        buildQueryFromState({
          phase: selectedPhase,
          statuses: selectedStatuses,
          jobCardStatuses: selectedJobCardStatuses,
          invoiceStatuses: selectedInvoiceStatuses,
          projectTypes: selectedProjectTypes,
          clientIds: nextFilters.clientId || [],
          archived: value,
          sortField: nextFilters.sortField,
          sortDirection: nextFilters.sortDirection,
          search: searchText,
          showDeleted,
          dateField: dateField !== 'statusDate' ? dateField : undefined,
          dateFrom: dateFrom || undefined,
          dateTo: dateTo || undefined,
        })
      );
      return nextFilters;
    });
  };

  const handleDeletedChange = (value: boolean) => {
    setShowDeleted(value);
    setFilters(prev => {
      const nextFilters = {
        ...prev,
        showDeleted: value ? 'only' as const : undefined,
      };
      updateQueryString(
        buildQueryFromState({
          phase: selectedPhase,
          statuses: selectedStatuses,
          jobCardStatuses: selectedJobCardStatuses,
          invoiceStatuses: selectedInvoiceStatuses,
          projectTypes: selectedProjectTypes,
          clientIds: nextFilters.clientId || [],
          archived: showArchived,
          sortField: nextFilters.sortField,
          sortDirection: nextFilters.sortDirection,
          search: searchText,
          showDeleted: value,
          dateField: dateField !== 'statusDate' ? dateField : undefined,
          dateFrom: dateFrom || undefined,
          dateTo: dateTo || undefined,
        })
      );
      return nextFilters;
    });
  };

  const handleDateFilterChange = (field: string, from: string, to: string) => {
    setDateField(field);
    setDateFrom(from);
    setDateTo(to);
    setDatePreset('');
  };

  const handleDatePreset = (preset: DatePreset) => {
    setDatePreset(preset);
    const today = new Date();
    let from = '';
    switch (preset) {
      case 'last7': from = format(subDays(today, 7), 'yyyy-MM-dd'); break;
      case 'last30': from = format(subDays(today, 30), 'yyyy-MM-dd'); break;
      case 'last90': from = format(subDays(today, 90), 'yyyy-MM-dd'); break;
      default: break;
    }
    setDateFrom(from);
    setDateTo('');
  };

  const clearDateFilter = () => {
    setDateField('statusDate');
    setDateFrom('');
    setDateTo('');
    setDatePreset('');
  };

  // Apply default profile only on initial page load (not after clearing filters)
  useEffect(() => {
    if (hasAppliedDefaultProfile.current) return;
    if (location.search) {
      hasAppliedDefaultProfile.current = true;
      return;
    }
    const defaultProfile = filterProfiles.find(profile => profile.isDefault);
    if (profilesLoading || !defaultProfile) return;

    hasAppliedDefaultProfile.current = true;
    navigate({ pathname: location.pathname, search: defaultProfile.query }, { replace: true });
  }, [filterProfiles, location.pathname, location.search, navigate, profilesLoading]);

  const parsedQuery = useMemo(
    () => parseJobListQuery(location.search),
    [location.search]
  );

  useEffect(() => {
    const {
      phase,
      statuses,
      jobCardStatuses,
      invoiceStatuses,
      projectTypes,
      clientIds,
      search,
      sortField,
      sortDirection,
      archived,
      showDeleted: parsedShowDeleted,
      dateField: parsedDateField,
      dateFrom: parsedDateFrom,
      dateTo: parsedDateTo,
    } = parsedQuery;

    setSelectedPhase(prev => (prev === phase ? prev : phase));
    setSelectedStatuses(prev => (isSameArray(prev, statuses) ? prev : statuses));
    setSelectedJobCardStatuses(prev =>
      isSameArray(prev, jobCardStatuses) ? prev : jobCardStatuses
    );
    setSelectedInvoiceStatuses(prev =>
      isSameArray(prev, invoiceStatuses) ? prev : invoiceStatuses
    );
    setSelectedProjectTypes(prev =>
      isSameArray(prev, projectTypes) ? prev : projectTypes
    );
    if (clientIds.length === 0) {
      setSelectedClients(prev => (prev.length === 0 ? prev : []));
    }
    setSearchText(prev => (prev.trim() === search ? prev : search));
    setShowArchived(prev => (prev === archived ? prev : archived));
    setShowDeleted(prev => (prev === parsedShowDeleted ? prev : parsedShowDeleted));
    setDateField(prev => {
      const next = parsedDateField || 'statusDate';
      return prev === next ? prev : next;
    });
    setDateFrom(prev => {
      const next = parsedDateFrom || '';
      return prev === next ? prev : next;
    });
    setDateTo(prev => {
      const next = parsedDateTo || '';
      return prev === next ? prev : next;
    });

    const nextFilters = buildFiltersFromState({
      phase,
      statuses,
      jobCardStatuses,
      invoiceStatuses,
      projectTypes,
      clientIds,
      archived,
      sortField,
      sortDirection,
      showDeleted: parsedShowDeleted,
      dateField: parsedDateField,
      dateFrom: parsedDateFrom,
      dateTo: parsedDateTo,
      search,
    });
    setFilters(prev => (areFiltersEqual(prev, nextFilters) ? prev : nextFilters));
  }, [parsedQuery]);

  // Check if active profile query matches current query (for detecting modifications)
  const isProfileModified = useMemo(() => {
    if (!activeProfileName) return false;
    const activeProfile = filterProfiles.find(p => p.name === activeProfileName);
    if (!activeProfile) return false;
    return normalizeQuery(activeProfile.query) !== normalizeQuery(location.search);
  }, [activeProfileName, filterProfiles, location.search]);

  // Only auto-detect profile on initial load or when no profile is explicitly selected
  useEffect(() => {
    // If a profile is already selected (even if modified), don't auto-switch
    if (activeProfileName) return;

    const normalizedQuery = normalizeQuery(location.search);
    const matchingProfile = filterProfiles.find(
      profile => normalizeQuery(profile.query) === normalizedQuery
    );
    if (matchingProfile) {
      setActiveProfileName(matchingProfile.name);
    }
  }, [filterProfiles, location.search, activeProfileName]);

  useEffect(() => {
    if (!filters.clientId || filters.clientId.length === 0 || !clients) return;
    const idToName = new Map<string, string>();
    clients.forEach(client => {
      idToName.set(client.id, formatClientName(client.name, client.firstName, client.lastName));
    });
    const names = filters.clientId
      .map(id => idToName.get(id))
      .filter((name): name is string => Boolean(name));
    if (names.length > 0) {
      const uniqueNames = Array.from(new Set(names));
      setSelectedClients(prev => {
        const isSame =
          uniqueNames.length === prev.length &&
          uniqueNames.every((name, idx) => name === prev[idx]);
        return isSame ? prev : uniqueNames;
      });
    }
  }, [filters.clientId, clients]);

  const saveProfile = async (name: string, makeDefault: boolean) => {
    if (!name.trim()) return;
    const clientIds = selectedClients
      .map(name => clientNameToId.get(name))
      .filter((id): id is string => id !== undefined);
    const baseQuery = buildQueryFromState({
      phase: selectedPhase,
      statuses: selectedStatuses,
      jobCardStatuses: selectedJobCardStatuses,
      invoiceStatuses: selectedInvoiceStatuses,
      projectTypes: selectedProjectTypes,
      clientIds,
      archived: showArchived,
      sortField: filters.sortField,
      sortDirection: filters.sortDirection,
      search: searchText,
      showDeleted,
      dateField: dateField !== 'statusDate' ? dateField : undefined,
      dateFrom: dateFrom || undefined,
      dateTo: dateTo || undefined,
    });
    const trimmedName = name.trim();
    const profile = await saveProfileMutation.mutateAsync({
      name: trimmedName,
      query: baseQuery,
      isDefault: makeDefault,
    });
    setActiveProfileName(profile.name);
  };

  const updateProfile = async (name: string) => {
    const clientIds = selectedClients
      .map(name => clientNameToId.get(name))
      .filter((id): id is string => id !== undefined);
    const baseQuery = buildQueryFromState({
      phase: selectedPhase,
      statuses: selectedStatuses,
      jobCardStatuses: selectedJobCardStatuses,
      invoiceStatuses: selectedInvoiceStatuses,
      projectTypes: selectedProjectTypes,
      clientIds,
      archived: showArchived,
      sortField: filters.sortField,
      sortDirection: filters.sortDirection,
      search: searchText,
      showDeleted,
      dateField: dateField !== 'statusDate' ? dateField : undefined,
      dateFrom: dateFrom || undefined,
      dateTo: dateTo || undefined,
    });
    const existingProfile = filterProfiles.find(p => p.name === name);
    if (!existingProfile) return;

    await saveProfileMutation.mutateAsync({
      name,
      query: baseQuery,
      isDefault: existingProfile.isDefault,
    });
  };

  const deleteProfile = async (id: string, name: string) => {
    await deleteProfileMutation.mutateAsync(id);
    if (activeProfileName === name) {
      setActiveProfileName('');
    }
  };

  const setDefaultProfile = async (id: string, name: string) => {
    await setDefaultMutation.mutateAsync(id);
    setActiveProfileName(name);
  };

  const handleProfileChange = (name: string) => {
    setActiveProfileName(name);
    const profile = filterProfiles.find(item => item.name === name);
    if (!profile) return;
    updateQueryString(profile.query, false);
  };

  const resetToProfile = () => {
    const profile = filterProfiles.find(item => item.name === activeProfileName);
    if (!profile) return;
    updateQueryString(profile.query, false);
  };

  return {
    jobs,
    isLoading,
    isFetching,
    error,
    filters,
    availableClients,
    isDrawerOpen,
    isAnimating,
    selectedPhase,
    selectedStatuses,
    selectedJobCardStatuses,
    selectedInvoiceStatuses,
    selectedClients,
    selectedProjectTypes,
    searchText,
    showArchived,
    showDeleted,
    dateField,
    dateFrom,
    dateTo,
    datePreset,
    filterProfiles,
    activeProfileName,
    isProfileModified,
    isProfileModalOpen,
    setIsProfileModalOpen,
    openDrawer,
    closeDrawer,
    handleSort,
    applyFilters,
    clearFilters,
    handlePhaseChange,
    handleSearchChange,
    handleArchivedChange,
    handleDeletedChange,
    handleDateFilterChange,
    handleDatePreset,
    clearDateFilter,
    handleProfileChange,
    resetToProfile,
    saveProfile,
    updateProfile,
    deleteProfile,
    setDefaultProfile,
    setSelectedStatuses,
    setSelectedJobCardStatuses,
    setSelectedInvoiceStatuses,
    setSelectedClients,
    setSelectedProjectTypes,
    setShowArchived,
    setShowDeleted,
    presetChips: PRESET_CHIPS,
  };
}
